import { cn, useAutoFocus } from "@istari-frontend/shared";
import { Avatar, AvatarFallback, Badge, Button, Label } from "@istari-frontend/ui";
import { Command as CommandPrimitive } from "cmdk";
import { Check, Plus, X } from "lucide-react";
import { type ReactNode, useDeferredValue, useId, useRef, useState } from "react";

import { validateEmail } from "~/utils/strings";

type User = {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  [key: string]: unknown;
};

type UserSearchInputProps = {
  availableUsers: User[];
  onSelectUser: (user: User) => void;
  onCreateEmail?: (email: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  labelExtra?: ReactNode;
  autoFocus?: boolean;
};

export function UserSearchInput({
  availableUsers,
  onSelectUser,
  onCreateEmail,
  isLoading = false,
  placeholder = "Search users or enter email...",
  label = "Add users",
  disabled = false,
  labelExtra,
  autoFocus = false,
}: UserSearchInputProps) {
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useAutoFocus(inputRef, autoFocus);

  // Defer only the expensive availableUsers filter so each keystroke renders the input
  // synchronously; the email-create/validation UI below stays on the immediate `search`.
  const deferredSearch = useDeferredValue(search);
  const normalizedSearch = deferredSearch.trim().toLowerCase();
  const filteredUsers = normalizedSearch
    ? availableUsers.filter((u) => {
        const haystack = [u.firstName, u.lastName, u.email].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(normalizedSearch);
      })
    : availableUsers;

  const trimmedSearch = search.trim();
  const shouldShowCreateOption = onCreateEmail && trimmedSearch && filteredUsers.length === 0;
  const isValidEmail = validateEmail(trimmedSearch);

  const handleSelect = (value: string) => {
    if (disabled || isLoading) {
      return;
    }

    if (value.startsWith("email:")) {
      const email = value.slice(6);
      onCreateEmail?.(email);
    } else {
      const user = availableUsers.find((u) => u.id === value);
      if (user) {
        onSelectUser(user);
      }
    }
    setSearch("");
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={searchInputId} className="text-sm">
          {label}
        </Label>
        {labelExtra}
      </div>
      <div className="relative">
        <CommandPrimitive
          shouldFilter={false}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              inputRef.current?.blur();
            }
            if ((e.key === "ArrowDown" || e.key === "ArrowRight") && !open) {
              setOpen(true);
            }
          }}
        >
          <div
            className={cn(
              "focus-within:ring-ring focus-within:border-ring flex h-9 items-center gap-2 rounded-md border border-b-[0.5px] py-3 pr-2 pl-3 focus-within:ring-2 focus-within:ring-offset-2",
              open && "rounded-b-none !border-b-0 shadow-md",
            )}
          >
            <CommandPrimitive.Input
              ref={inputRef}
              id={searchInputId}
              data-testid="user-search-input"
              placeholder={placeholder}
              value={search}
              onValueChange={(value) => {
                setSearch(value);
                if (!open && value.trim()) {
                  setOpen(true);
                }
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              disabled={disabled || isLoading}
              className="placeholder:text-muted-foreground flex h-10 w-full border-0 bg-transparent px-0 text-sm !shadow-none outline-none placeholder:text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Add user"
              type="button"
              disabled={disabled || isLoading}
              className="[&_svg]:text-muted-foreground size-6 opacity-50 hover:opacity-100 focus:opacity-100"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => inputRef.current?.focus()}
            >
              <Plus className="size-4" />
            </Button>
          </div>

          {open && (
            <CommandPrimitive.List className="bg-background absolute top-full right-0 left-0 z-50 mt-[-1px] max-h-48 overflow-y-auto rounded-b-md border-[0.5px] border-gray-200 shadow-md">
              {isLoading ? (
                <div className="text-muted-foreground p-2 text-sm">Loading users...</div>
              ) : filteredUsers.length === 0 && !shouldShowCreateOption ? (
                <CommandPrimitive.Empty className="text-muted-foreground p-2 text-sm">
                  {search.trim() ? "No matching users found." : "No users available."}
                </CommandPrimitive.Empty>
              ) : (
                <div>
                  {shouldShowCreateOption && (
                    <CommandPrimitive.Item
                      value={`email:${trimmedSearch}`}
                      onSelect={handleSelect}
                      onMouseDown={(e) => e.preventDefault()}
                      className="px-2 py-1.5 text-sm outline-hidden data-[selected=true]:bg-gray-100"
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-0.5">
                          <span className="text-foreground text-xs">Invite email user:</span>
                          <Badge variant="outline" size="sm" className="bg-white px-1 py-0 italic">
                            {trimmedSearch}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          {isValidEmail ? (
                            <>
                              <Check className="size-3 text-green-600" />
                              <span className="text-muted-foreground">Valid email address</span>
                            </>
                          ) : (
                            <>
                              <X className="text-muted-foreground size-3" />
                              <span className="text-muted-foreground">Enter a valid email address</span>
                            </>
                          )}
                        </div>
                      </div>
                    </CommandPrimitive.Item>
                  )}

                  {filteredUsers.map((u) => (
                    <CommandPrimitive.Item
                      key={u.id}
                      value={u.id || ""}
                      onSelect={handleSelect}
                      onMouseDown={(e) => e.preventDefault()}
                      className="group px-2 py-1.5 text-sm outline-hidden data-[selected=true]:bg-gray-100"
                    >
                      <div className="flex items-center gap-1.5">
                        <Avatar size="xs">
                          <AvatarFallback
                            size="xs"
                            className="transition-colors duration-0 ease-out group-data-[selected=true]:bg-white group-data-[selected=true]:duration-100"
                          >{`${u.firstName?.[0] || ""}${u.lastName?.[0] || ""}`}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="text-xs">
                            {u.firstName} {u.lastName}
                          </span>
                          {u.email && <span className="text-muted-foreground text-xs font-light">{u.email}</span>}
                        </div>
                      </div>
                    </CommandPrimitive.Item>
                  ))}
                </div>
              )}
            </CommandPrimitive.List>
          )}
        </CommandPrimitive>
      </div>
    </div>
  );
}
